import type { ButtonProps } from "react-aria-components";
import { Button as ReactAriaButton } from "react-aria-components";

import { IcClose } from "@jetblue/kit-icons/ic-close";
import { cn } from "../../utilities/tailwind";

type CloseButtonProperties = {
  /**
   * Classes that are applied to the button.
   */
  className?: string | undefined;

  /**
   * Classes that are applied to the icon.
   */
  iconClassName?: string | undefined;

  /**
   * Classes that are applied to the child of the button.
   */
  innerClassName?: string | undefined;

  /**
   * Sizes: `small` | `large`
   */
  size?: CloseButtonSize | undefined;

  /**
   * Shapes: `circle` | `circle-borderless` | `square`
   */
  shape?: CloseButtonShape | undefined;

  /**
   * Variants: `blue` | `white`
   */
  variant?: CloseButtonVariant | undefined;
} & Omit<
  ButtonProps,
  "children" | "className" | "isDisabled" | "isPending" | "style"
>;
type CloseButtonShape = "circle" | "circle-borderless" | "square";
type CloseButtonSize = "large" | "small";
type CloseButtonVariant = "blue" | "white";

export type CloseButtonStyles = {
  base: string;
  color: Record<CloseButtonVariant, string>;
  icon: Record<"size", Record<CloseButtonSize, string>>;
  shape: Record<"circular" | "square", string>;
};
const CLOSE_BUTTON_INNER_CIRCLE_FOCUS_STYLES = cn(
  "group-focus-visible:bg-core-blue group-focus-visible:outline group-focus-visible:outline-offset-[3px] group-focus-visible:outline-core-blue",
);

export const CLOSE_BUTTON_STYLES = {
  base: cn(
    "group flex size-11 items-center justify-center bg-core-blue text-white hover:bg-royal-blue",
  ),
  color: {
    blue: cn(""),
    white: cn(
      "bg-white text-core-blue hover:bg-core-blue hover:text-white focus-visible:bg-core-blue focus-visible:text-white",
    ),
  },
  icon: { size: { large: cn(""), small: cn("size-3") } },
  shape: {
    circular: cn(
      "bg-transparent hover:bg-transparent focus-visible:bg-transparent focus-visible:outline-none",
    ),
    square: cn(
      "focus-visible:bg-royal-blue focus-visible:outline focus-visible:outline-offset-[3px] focus-visible:outline-royal-blue",
    ),
  },
} satisfies CloseButtonStyles;
export type CloseButtonInnerStyles = {
  base: string;
  color: Record<CloseButtonVariant, string>;
  shape: Record<CloseButtonShape, string>;
  size: Record<CloseButtonSize, string>;
};
export const CLOSE_BUTTON_INNER_STYLES = {
  base: cn(
    "flex size-11 items-center justify-center rounded-full bg-royal-blue group-hover:bg-core-blue group-focus-visible:border-core-blue",
  ),
  color: {
    blue: cn(""),
    white: cn(
      "border-core-blue bg-white text-core-blue group-hover:bg-core-blue group-hover:text-white group-focus-visible:border-core-blue group-focus-visible:bg-core-blue group-focus-visible:text-white",
    ),
  },
  shape: {
    circle: cn(
      `border-2 border-white ${CLOSE_BUTTON_INNER_CIRCLE_FOCUS_STYLES}`,
    ),
    "circle-borderless": cn(
      `border-none ${CLOSE_BUTTON_INNER_CIRCLE_FOCUS_STYLES}`,
    ),
    square: cn(
      "bg-inherit text-inherit group-hover:bg-inherit group-hover:text-inherit",
    ),
  },
  size: { large: cn(""), small: cn("size-8") },
} satisfies CloseButtonInnerStyles;
export const CloseButton = ({
  className = "",
  iconClassName = "",
  innerClassName = "",
  shape = "square",
  size = "large",
  variant = "blue",
  ...properties
}: CloseButtonProperties) => {
  const hasLightBackground = variant === "white";
  const isCircle = shape === "circle";
  const isCircleBorderless = shape === "circle-borderless";
  const isSmall = size === "small";
  const isSquare = shape === "square";

  const twClassName = cn(
    CLOSE_BUTTON_STYLES.base,
    { [CLOSE_BUTTON_STYLES.shape.square]: isSquare },
    { [CLOSE_BUTTON_STYLES.color.white]: hasLightBackground },
    { [CLOSE_BUTTON_STYLES.shape.circular]: isCircle || isCircleBorderless },
    className,
  );

  const twClassNameInner = cn(
    CLOSE_BUTTON_INNER_STYLES.base,
    { [CLOSE_BUTTON_INNER_STYLES.shape.circle]: isCircle },
    {
      [CLOSE_BUTTON_INNER_STYLES.shape["circle-borderless"]]:
        isCircleBorderless,
    },
    { [CLOSE_BUTTON_INNER_STYLES.color.white]: hasLightBackground },
    { [CLOSE_BUTTON_INNER_STYLES.shape.square]: isSquare },
    { [CLOSE_BUTTON_INNER_STYLES.size.small]: isSmall },
    innerClassName,
  );

  const twIconClassName = cn(
    { [CLOSE_BUTTON_STYLES.icon.size.small]: isSmall },
    iconClassName,
  );

  return (
    <ReactAriaButton aria-label="Close" className={twClassName} {...properties}>
      <div className={twClassNameInner}>
        <IcClose className={twIconClassName} />
      </div>
    </ReactAriaButton>
  );
};
CloseButton.displayName = "CloseButton";
